"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

let pluginsRegistered = false;
const numberPattern = /\d+/g;

function animateCount(element: HTMLElement) {
    const animationsType = element.dataset.animationType || "normal";
    const template = element.textContent?.trim();

    if (!template) return;

    const matches = [...template.matchAll(numberPattern)];

    if (!matches.length) return;

    const state = { progress: 0 };
    const targets = matches.map(([value]) => Number(value));

    const render = () => {
        let index = 0;

        element.textContent = template.replace(numberPattern, (value) => {
            const target = targets[index];
            const startValue = animationsType === "slow" ? Math.floor(target * 0.8) : 0;
            const current = Math.round(startValue + (target - startValue) * state.progress);
            index += 1;

            return String(current).padStart(value.length, "0");
        });
    };

    render();

    gsap.to(state, {
        progress: 1,
        duration: animationsType === "slow" ? 10 : 1.5,
        onUpdate: render,
        scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none reverse",
        },
    });
}

function animateConicProgress(element: HTMLElement) {
    const targetRaw = Number(element.dataset.progressTarget ?? "0");
    const target = gsap.utils.clamp(0, 100, targetRaw);
    const state = { progress: 0 };

    const render = () => {
        element.style.background = `conic-gradient(#45ACE6 ${state.progress}%, transparent 0)`;
    };

    render();

    gsap.to(state, {
        progress: target,
        duration: 1,
        ease: "power2.out",
        onUpdate: render,
        scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none reverse",
        },
    });
}

export default function GsapHeadingAnimations() {
    const pathname = usePathname();

    useEffect(() => {
        if (!pluginsRegistered) {
            gsap.registerPlugin(ScrollTrigger, SplitText);
            pluginsRegistered = true;
        }

        const splits: SplitText[] = [];

        const ctx = gsap.context(() => {
            const headings = document.querySelectorAll<HTMLElement>("h2");
            const counts = document.querySelectorAll<HTMLElement>(".gsap-counting");
            const conicProgressItems = document.querySelectorAll<HTMLElement>("[data-conic-progress]");

            headings.forEach((heading) => {
                const split = SplitText.create(heading, {
                    type: "words",
                    wordsClass: "word++",
                    tag: "span",
                });

                splits.push(split);

                gsap.set(split.words, {
                    display: "inline-block",
                    opacity: 0,
                    y: 24,
                    willChange: "transform, opacity",
                });

                gsap.to(split.words, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: 0.06,
                    scrollTrigger: {
                        trigger: heading,
                        start: "top 80%",
                        toggleActions: "play none none reverse",
                    },
                });
            });

            counts.forEach(animateCount);
            conicProgressItems.forEach(animateConicProgress);
        });



        return () => {
            ctx.revert();
            splits.forEach((split) => split.revert());
        };
    }, [pathname]);

    return null;
}